import { useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Table } from '@ohpen/enso-ui';

import { ConfigTableActions } from '@config/components';
import { useConfigContext } from '@config/context';
import { CONFIG_PAGINATION, CONFIG_SCROLL } from '@config/utils';
import type { RoleRow } from '../interfaces';
import { getOriginTagMappings, ORIGIN_LIST_NAME, roleColumns, rolePermissionColumns } from '../tableConfig';
import { originTag } from '../utils';

export const RolesList = (): ReactElement => {
  const { t } = useTranslation();
  const { configuration, roleCatalogue } = useConfigContext();

  const originList = useMemo(() => ({ provided: t('config.roles.origin.provided'), client: t('config.roles.origin.client') }), [t]);

  const dataSource: RoleRow[] = useMemo(() => {
    const entries =
      roleCatalogue ??
      Object.entries(configuration?.groupPermissions ?? {}).map(([role, resources]) => ({
        role,
        provided: true,
        permissions: Object.entries(resources).flatMap(([resource, actions]) => actions.map(action => `${resource}:${action}`)),
      }));

    return entries
      .map(entry => ({
        role: entry.role,
        group: 'group' in entry ? entry.group : undefined,
        description: 'description' in entry ? entry.description : undefined,
        deprecated: 'deprecated' in entry ? entry.deprecated : undefined,
        origin: originTag(entry.provided ? 'provided' : 'client', originList),
        count: entry.permissions.length,
        permissions: [...entry.permissions]
          .sort((a, b) => a.localeCompare(b))
          .map(permission => {
            const [resource, action = ''] = permission.split(':');
            return { permission, resource, action };
          }),
      }))
      .sort((a, b) => a.role.localeCompare(b.role));
  }, [configuration, originList, roleCatalogue]);

  const valueLists = useMemo(() => ({ [ORIGIN_LIST_NAME]: originList }), [originList]);

  return (
    <Table
      id="configRoles"
      rowKey="role"
      dataSource={dataSource}
      columns={roleColumns}
      valueLists={valueLists}
      tagMappings={getOriginTagMappings()}
      expanded={{ rowKey: 'permission', columnsKey: 'permissions', columns: rolePermissionColumns }}
      pagination={CONFIG_PAGINATION}
      scroll={CONFIG_SCROLL}
      itemsLabel={t('config.sections.roles')}
    >
      <ConfigTableActions />
    </Table>
  );
};
